import logger from '../utils/logger';
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { settingsService } from '../services/settingsService';
import { useSettings } from './SettingsContext';

const AutoSaveContext = createContext();

export const useAutoSave = () => {
  const context = useContext(AutoSaveContext);
  if (!context) {
    throw new Error('useAutoSave must be used within an AutoSaveProvider');
  }
  return context;
};

export const AutoSaveProvider = ({ children }) => {
  const { autoSaveSettings, isLoading: settingsLoading } = useSettings();
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [autoSaveInterval, setAutoSaveInterval] = useState(30); // seconds
  const [isLoading, setIsLoading] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [savingStatus, setSavingStatus] = useState('idle'); // idle, saving, saved, error

  const autoSaveTimeoutRef = useRef(null);
  const saveCallbacksRef = useRef(new Map());

  // Load auto-save settings from centralized SettingsContext
  useEffect(() => {
    if (!settingsLoading && autoSaveSettings) {
      if (autoSaveSettings.auto_save !== undefined) {
        setAutoSaveEnabled(autoSaveSettings.auto_save);
      }
      if (autoSaveSettings.auto_save_interval !== undefined) {
        setAutoSaveInterval(autoSaveSettings.auto_save_interval);
      }
      setIsLoading(false);
    }
  }, [autoSaveSettings, settingsLoading]);

  // Update auto-save enabled setting
  const updateAutoSaveEnabled = async (enabled) => {
    try {
      setAutoSaveEnabled(enabled);
      
      // Save to settings
      await settingsService.updateSettingsBulk('general', {
        auto_save: enabled
      });

      // Clear pending auto-save if disabled
      if (!enabled && autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }
    } catch (error) {
      logger.error('Failed to save auto-save enabled setting:', error);
      await loadAutoSaveSettings();
    }
  };

  // Update auto-save interval setting
  const updateAutoSaveInterval = async (interval) => {
    try {
      setAutoSaveInterval(interval);
      
      // Save to settings
      await settingsService.updateSettingsBulk('general', {
        auto_save_interval: interval
      });

      // Restart auto-save timer with new interval
      if (autoSaveEnabled && isDirty) {
        scheduleAutoSave();
      }
    } catch (error) {
      logger.error('Failed to save auto-save interval setting:', error);
      await loadAutoSaveSettings();
    }
  };

  // Schedule auto-save
  const scheduleAutoSave = useCallback(() => {
    if (!autoSaveEnabled || saveCallbacksRef.current.size === 0) {
      return;
    }

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Schedule new auto-save
    autoSaveTimeoutRef.current = setTimeout(() => {
      performAutoSave();
    }, autoSaveInterval * 1000);
  }, [autoSaveEnabled, autoSaveInterval]);

  // Perform auto-save
  const performAutoSave = async () => {
    if (!autoSaveEnabled || !isDirty || saveCallbacksRef.current.size === 0) {
      return;
    }

    setSavingStatus('saving');
    
    try {
      // Execute all registered save callbacks
      const savePromises = Array.from(saveCallbacksRef.current.values()).map(
        callback => callback()
      );
      
      await Promise.all(savePromises);
      
      setLastSaved(new Date());
      setIsDirty(false);
      setSavingStatus('saved');
      
      // Reset status after 2 seconds
      setTimeout(() => {
        setSavingStatus('idle');
      }, 2000);
      
    } catch (error) {
      logger.error('Auto-save failed:', error);
      setSavingStatus('error');
      
      // Reset status after 3 seconds
      setTimeout(() => {
        setSavingStatus('idle');
      }, 3000);
    }
  };

  // Register a component for auto-save
  const register = useCallback((id, saveCallback) => {
    saveCallbacksRef.current.set(id, saveCallback);
    
    // Return unregister function
    return () => {
      saveCallbacksRef.current.delete(id);
    };
  }, []);

  // Mark data as dirty (needs saving)
  const markDirty = useCallback(() => {
    if (!isDirty) {
      setIsDirty(true);
      setSavingStatus('idle');
    }
    
    if (autoSaveEnabled) {
      scheduleAutoSave();
    }
  }, [isDirty, autoSaveEnabled, scheduleAutoSave]);

  // Mark data as clean (saved)
  const markClean = useCallback(() => {
    setIsDirty(false);
    setLastSaved(new Date());
    setSavingStatus('saved');
    
    // Clear auto-save timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }

    // Reset status after 2 seconds
    setTimeout(() => {
      setSavingStatus('idle');
    }, 2000);
  }, []);

  // Force save now
  const saveNow = useCallback(async () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
    
    await performAutoSave();
  }, []);

  // Get time since last save
  const getTimeSinceLastSave = useCallback(() => {
    if (!lastSaved) return null;
    
    const now = new Date();
    const diffMs = now - lastSaved;
    const diffSeconds = Math.floor(diffMs / 1000);
    
    if (diffSeconds < 60) {
      return `${diffSeconds}s ago`;
    } else if (diffSeconds < 3600) {
      const minutes = Math.floor(diffSeconds / 60);
      return `${minutes}m ago`;
    } else {
      const hours = Math.floor(diffSeconds / 3600);
      return `${hours}h ago`;
    }
  }, [lastSaved]);

  // Get auto-save status message
  const getStatusMessage = useCallback(() => {
    switch (savingStatus) {
      case 'saving':
        return 'Auto-saving...';
      case 'saved':
        return `Saved ${getTimeSinceLastSave()}`;
      case 'error':
        return 'Auto-save failed';
      default:
        if (isDirty && autoSaveEnabled) {
          return 'Changes pending...';
        } else if (lastSaved) {
          return `Last saved ${getTimeSinceLastSave()}`;
        }
        return null;
    }
  }, [savingStatus, isDirty, autoSaveEnabled, getTimeSinceLastSave]);

  // Get status color
  const getStatusColor = useCallback(() => {
    switch (savingStatus) {
      case 'saving':
        return 'text-blue-600';
      case 'saved':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      default:
        if (isDirty && autoSaveEnabled) {
          return 'text-yellow-600';
        }
        return 'text-gray-500';
    }
  }, [savingStatus, isDirty, autoSaveEnabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  const value = {
    autoSaveEnabled,
    autoSaveInterval,
    isLoading,
    isDirty,
    savingStatus,
    lastSaved,
    updateAutoSaveEnabled,
    updateAutoSaveInterval,
    register,
    markDirty,
    markClean,
    saveNow,
    getTimeSinceLastSave,
    getStatusMessage,
    getStatusColor
  };

  return (
    <AutoSaveContext.Provider value={value}>
      {children}
    </AutoSaveContext.Provider>
  );
};

export default AutoSaveContext;